import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from "../../api_config.json";

const { Title, Text } = Typography;
const { TextArea } = Input;

const slugRegex = /^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/;

const emptyPack = {
  packId: "",
  name: "",
  description: "",
  creditsAmount: 10,
  priceCents: 500,
  isActive: true,
};

const formatEur = (cents) =>
  ((Number(cents) || 0) / 100).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
  });

const CreditPacks = () => {
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";
  const { getTokenSilently } = useAuth0();
  const [form] = Form.useForm();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPack, setEditingPack] = useState(null);

  const apiRequest = useCallback(
    async (path, options = {}) => {
      const token = await getTokenSilently();
      const response = await fetch(`${config.baseUrl[env]}${path}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...(options.headers || {}),
        },
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Operazione pacchetto non riuscita");
      }

      return data;
    },
    [env, getTokenSilently],
  );

  const fetchPacks = useCallback(async () => {
    setLoading(true);

    try {
      const data = await apiRequest("/admin/credit-packs");
      setPacks(data.data || []);
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiRequest]);

  useEffect(() => {
    fetchPacks();
  }, [fetchPacks]);

  const openCreateModal = () => {
    setEditingPack(null);
    form.setFieldsValue(emptyPack);
    setModalOpen(true);
  };

  const openEditModal = (pack) => {
    setEditingPack(pack);
    form.setFieldsValue({
      ...emptyPack,
      ...pack,
      isActive: Boolean(pack.isActive),
    });
    setModalOpen(true);
  };

  const savePack = async () => {
    const values = await form.validateFields();

    setSaving(true);

    try {
      if (editingPack) {
        const patch = {
          name: values.name,
          description: values.description || "",
          priceCents: values.priceCents,
          isActive: values.isActive,
        };
        await apiRequest(
          `/admin/credit-packs/${encodeURIComponent(editingPack.packId)}`,
          {
            method: "PATCH",
            body: JSON.stringify(patch),
          },
        );
        message.success("Pacchetto aggiornato.");
      } else {
        await apiRequest("/admin/credit-packs", {
          method: "POST",
          body: JSON.stringify({
            packId: values.packId.trim().toLowerCase(),
            name: values.name,
            description: values.description || "",
            creditsAmount: values.creditsAmount,
            priceCents: values.priceCents,
            isActive: values.isActive,
          }),
        });
        message.success("Pacchetto creato e sincronizzato con Stripe.");
      }

      setModalOpen(false);
      await fetchPacks();
    } catch (err) {
      message.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const archivePack = async (pack) => {
    try {
      await apiRequest(
        `/admin/credit-packs/${encodeURIComponent(pack.packId)}`,
        { method: "DELETE" },
      );
      message.success("Pacchetto archiviato.");
      await fetchPacks();
    } catch (err) {
      message.error(err.message);
    }
  };

  const togglePackStatus = async (pack) => {
    try {
      await apiRequest(
        `/admin/credit-packs/${encodeURIComponent(pack.packId)}`,
        {
          method: "PATCH",
          body: JSON.stringify({ isActive: !pack.isActive }),
        },
      );
      message.success(
        !pack.isActive ? "Pacchetto attivato." : "Pacchetto disattivato.",
      );
      await fetchPacks();
    } catch (err) {
      message.error(err.message);
    }
  };

  const columns = [
    {
      title: "Pack ID",
      dataIndex: "packId",
      key: "packId",
      render: (value) => <code>{value}</code>,
    },
    {
      title: "Nome",
      dataIndex: "name",
      key: "name",
      render: (value) => <strong>{value}</strong>,
    },
    {
      title: "Crediti",
      dataIndex: "creditsAmount",
      key: "creditsAmount",
      sorter: (a, b) => (a.creditsAmount || 0) - (b.creditsAmount || 0),
      render: (value) => `${value} crediti AI`,
    },
    {
      title: "Prezzo (IVA escl.)",
      dataIndex: "priceCents",
      key: "priceCents",
      render: (value) => formatEur(value),
    },
    {
      title: "Stato",
      dataIndex: "isActive",
      key: "isActive",
      render: (value) => (
        <Tag color={value ? "green" : "default"}>
          {value ? "Attivo" : "Disattivo"}
        </Tag>
      ),
      filters: [
        { text: "Attivo", value: true },
        { text: "Disattivo", value: false },
      ],
      onFilter: (value, record) => Boolean(record.isActive) === value,
    },
    {
      title: "Stripe",
      key: "stripe",
      render: (_, record) =>
        record.stripeProductId ? (
          <Text copyable={{ text: record.stripeProductId }} type="secondary">
            {record.stripeProductId.slice(0, 12)}…
          </Text>
        ) : (
          <Tag color="orange">Non sincr.</Tag>
        ),
    },
    {
      title: "Azioni",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          >
            Modifica
          </Button>
          <Switch
            size="small"
            checked={Boolean(record.isActive)}
            onChange={() => togglePackStatus(record)}
            checkedChildren="On"
            unCheckedChildren="Off"
          />
          <Popconfirm
            title="Archivia il pacchetto e disattiva il prodotto Stripe?"
            okText="Archivia"
            cancelText="Annulla"
            onConfirm={() => archivePack(record)}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              Archivia
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <Space
          style={{ width: "100%", justifyContent: "space-between" }}
          align="center"
        >
          <Title level={3} style={{ margin: 0 }}>
            Pacchetti crediti AI
          </Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
          >
            Nuovo pacchetto
          </Button>
        </Space>
        <Text type="secondary">
          Configura i pacchetti acquistabili da Cards. La creazione e l'aggiornamento
          sincronizzano automaticamente Prodotto + Prezzo su Stripe (EUR, IVA esclusa).
        </Text>
      </div>

      <Table
        rowKey="packId"
        loading={loading}
        columns={columns}
        dataSource={packs}
        expandable={{
          expandedRowRender: (record) => (
            <div>
              {record.description && (
                <p>
                  <strong>Descrizione:</strong> {record.description}
                </p>
              )}
              <p>
                <strong>Stripe Product:</strong>{" "}
                <code>{record.stripeProductId || "-"}</code>
              </p>
              <p>
                <strong>Stripe Price:</strong>{" "}
                <code>{record.stripePriceId || "-"}</code>
              </p>
              <p>
                <strong>Creato:</strong> {record.createdAt || "-"} ·{" "}
                <strong>Aggiornato:</strong> {record.updatedAt || "-"}
                {record.archivedAt && (
                  <>
                    {" "}
                    · <strong>Archiviato:</strong> {record.archivedAt}
                  </>
                )}
              </p>
            </div>
          ),
        }}
      />

      <Modal
        visible={modalOpen}
        title={editingPack ? "Modifica pacchetto" : "Nuovo pacchetto crediti"}
        okText={editingPack ? "Salva" : "Crea"}
        cancelText="Annulla"
        confirmLoading={saving}
        onCancel={() => setModalOpen(false)}
        onOk={savePack}
        width={620}
      >
        <Form form={form} layout="vertical" initialValues={emptyPack}>
          <Form.Item
            name="packId"
            label="Pack ID (slug url-friendly)"
            tooltip="Permanente, non modificabile dopo la creazione. Es. starter-10, pro-50."
            rules={[
              { required: true, message: "Pack ID obbligatorio" },
              {
                pattern: slugRegex,
                message:
                  "Solo lettere minuscole, numeri e trattini (3-64 caratteri).",
              },
            ]}
          >
            <Input
              placeholder="es. starter-10"
              disabled={Boolean(editingPack)}
            />
          </Form.Item>
          <Form.Item
            name="name"
            label="Nome visualizzato"
            rules={[{ required: true, message: "Nome obbligatorio" }]}
          >
            <Input placeholder="es. Starter · 10 crediti AI" />
          </Form.Item>
          <Form.Item name="description" label="Descrizione">
            <TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
          <Space style={{ width: "100%" }} size="large" align="start">
            <Form.Item
              name="creditsAmount"
              label="Crediti AI"
              rules={[
                {
                  required: true,
                  message: "Numero crediti obbligatorio",
                },
              ]}
              style={{ minWidth: 180 }}
            >
              <InputNumber
                min={1}
                max={100000}
                style={{ width: "100%" }}
                disabled={Boolean(editingPack)}
              />
            </Form.Item>
            <Form.Item
              name="priceCents"
              label="Prezzo in centesimi (IVA escl.)"
              tooltip="500 = € 5,00"
              rules={[
                { required: true, message: "Prezzo obbligatorio" },
              ]}
              style={{ minWidth: 220 }}
            >
              <InputNumber
                min={1}
                max={1000000}
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item
              name="isActive"
              label="Attivo"
              valuePropName="checked"
              style={{ minWidth: 100 }}
            >
              <Switch checkedChildren="On" unCheckedChildren="Off" />
            </Form.Item>
          </Space>
          {editingPack && (
            <Text type="secondary">
              Modificare il prezzo crea un nuovo Stripe Price e disattiva il precedente
              (gli Stripe Price sono immutabili).
            </Text>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default CreditPacks;
